// CR183: S4 Normalize Advanced Diagnostics Accordion
// Shows live diagnostics even when RUNNING, with clear distinction between:
// - Extraction Prompt Pack (from staging provenance)
// - Normalize Rule Pack (from step resolution)
// CR184: Extended with lock state diagnostics
// CR186: Added Force Takeover button for stale locks

import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2, AlertCircle, Clock, Loader2, Lock, Unlock, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface S4AdvancedDiagnosticsProps {
  configJson: Record<string, unknown>;
  stepStatus: string;
  batchRunId?: string;
  stepId?: string;
  onForceTakeover?: () => void;
}

export function S4AdvancedDiagnostics({ 
  configJson, 
  stepStatus, 
  batchRunId,
  stepId,
  onForceTakeover,
}: S4AdvancedDiagnosticsProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  // Extract diagnostics from various possible locations
  const normalizeDiag = (configJson.normalize_diagnostics || {}) as Record<string, unknown>;
  const phase = (configJson.phase || normalizeDiag.phase || "unknown") as string;
  const heartbeatTs = (configJson.heartbeat_ts || normalizeDiag.heartbeat_ts) as string | undefined;
  const normalizeVersion = (configJson.normalize_version || normalizeDiag.normalize_version) as string | undefined;
  const invocationId = (configJson.invocation_id || normalizeDiag.invocation_id) as string | undefined;
  const traceId = (configJson.trace_id || normalizeDiag.trace_id) as string | undefined;

  // Rule pack info
  const rulePack = (normalizeDiag.rule_pack || {}) as Record<string, unknown>;
  const rulePackIdUsed = (configJson.rule_pack_id_used || configJson.rule_pack_id || rulePack.rule_pack_id_used) as string | null;
  const rulePackName = (configJson.rule_pack_name) as string | undefined;
  const rulePackSource = (configJson.rule_pack_source || rulePack.resolver_path_taken) as string | undefined;
  const resolverMs = (configJson.rule_pack_resolve_ms || rulePack.resolve_ms) as number | undefined;
  const resolverTimeout = (configJson.resolver_timeout || rulePack.resolver_timeout) as boolean | undefined;

  // CR184: Lock state info
  const lockState = (normalizeDiag.lock || {}) as Record<string, unknown>;
  const lockOutcome = lockState.outcome as string | undefined;
  const lockOwner = lockState.owner_invocation_id as string | undefined;
  const previousOwner = lockState.previous_owner_invocation_id as string | undefined;
  const previousHeartbeatAge = lockState.previous_heartbeat_age_seconds as number | undefined;
  const lockTtl = (lockState.ttl_seconds || 30) as number;
  const lockAcquiredAt = lockState.acquired_at as string | undefined;

  // CR186: Check for last conflict info
  const lastConflict = (configJson.last_conflict || {}) as Record<string, unknown>;
  const canTakeover = lastConflict.can_takeover === true;
  const conflictErrorKey = lastConflict.error_key as string | undefined;

  // Timers
  const timersMs = (normalizeDiag.timers_ms || {}) as Record<string, number>;

  // Sample prompt packs (extraction)
  const samplePromptPacks = (configJson.sample_prompt_packs || normalizeDiag.sample_prompt_packs || []) as Array<{
    staging_item_id: string;
    merchant_key: string;
    prompt_pack_id: string | null;
    prompt_pack_version: string | null;
    source_type: string;
  }>;

  // Heartbeat age calculation
  const heartbeatAge = heartbeatTs
    ? Math.round((Date.now() - new Date(heartbeatTs).getTime()) / 1000)
    : null;
  const isHeartbeatStale = heartbeatAge !== null && heartbeatAge > lockTtl;

  // CR186: Determine if we should show the Force Takeover button
  const showForceTakeover = (
    (stepStatus === "RUNNING" && isHeartbeatStale) ||
    canTakeover ||
    (lockOutcome === "LOCKED_BY_OTHER" && previousHeartbeatAge !== undefined && previousHeartbeatAge > 10)
  );

  const handleCopyDiagnostics = () => {
    const fullDiagnostics = {
      normalize_version: normalizeVersion,
      invocation_id: invocationId,
      trace_id: traceId,
      phase,
      heartbeat_ts: heartbeatTs,
      heartbeat_age_seconds: heartbeatAge,
      step_status: stepStatus,
      lock: {
        outcome: lockOutcome,
        owner: lockOwner,
        previous_owner: previousOwner,
        previous_heartbeat_age: previousHeartbeatAge,
        ttl_seconds: lockTtl,
        acquired_at: lockAcquiredAt,
      },
      rule_pack: {
        id: rulePackIdUsed,
        name: rulePackName,
        source: rulePackSource,
        resolve_ms: resolverMs,
        resolver_timeout: resolverTimeout,
      },
      timers_ms: timersMs,
      sample_prompt_packs: samplePromptPacks,
      last_conflict: lastConflict,
      full_normalize_diagnostics: normalizeDiag,
      full_config_json: configJson,
    };
    
    navigator.clipboard.writeText(JSON.stringify(fullDiagnostics, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: "Copied",
      description: "S4 diagnostics copied to clipboard",
    });
  };

  // CR184: Lock status indicator
  const getLockStatus = () => {
    if (!lockOutcome || lockOutcome === "NOT_CHECKED") {
      return { label: "Not checked", variant: "secondary" as const, icon: <Clock className="h-3 w-3" /> };
    }
    if (lockOutcome === "ACQUIRED") {
      return { label: "Acquired", variant: "outline" as const, icon: <Lock className="h-3 w-3" /> };
    }
    if (lockOutcome === "ACQUIRED_STALE_TAKEOVER") {
      return { label: "Takeover (stale)", variant: "warning" as const, icon: <Unlock className="h-3 w-3" /> };
    }
    if (lockOutcome === "LOCKED_BY_OTHER") {
      return { label: "Blocked by other", variant: "destructive" as const, icon: <AlertCircle className="h-3 w-3" /> };
    }
    return { label: lockOutcome, variant: "secondary" as const, icon: <Lock className="h-3 w-3" /> };
  };

  const lockStatus = getLockStatus();

  // Determine resolver status indicator
  const getResolverStatus = () => {
    if (resolverTimeout) {
      return { label: "Timed out (using fallback)", variant: "destructive" as const, icon: <AlertCircle className="h-3 w-3" /> };
    }
    if (rulePackIdUsed || rulePackSource === "embedded_default") {
      return { label: "Resolved", variant: "outline" as const, icon: <CheckCircle2 className="h-3 w-3" /> };
    }
    if (stepStatus === "RUNNING" && phase === "RESOLVE_RULE_PACK_START") {
      return { label: "Resolving...", variant: "secondary" as const, icon: <Loader2 className="h-3 w-3 animate-spin" /> };
    }
    return { label: "Pending", variant: "secondary" as const, icon: <Clock className="h-3 w-3" /> };
  };

  const resolverStatus = getResolverStatus();

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="advanced-diagnostics" className="border rounded-lg">
        <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
          <div className="flex items-center gap-2">
            <span className="font-medium">Advanced Diagnostics</span>
            {stepStatus === "RUNNING" && (
              <Badge variant={isHeartbeatStale ? "destructive" : "secondary"} className="text-[10px] gap-1">
                {isHeartbeatStale ? <AlertCircle className="h-3 w-3" /> : <Loader2 className="h-3 w-3 animate-spin" />}
                {phase}
              </Badge>
            )}
            {normalizeVersion && (
              <span className="text-xs text-muted-foreground font-mono">{normalizeVersion}</span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <div className="space-y-4">
            {/* CR186: Force Takeover Banner */}
            {showForceTakeover && onForceTakeover && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-warning">
                  <AlertCircle className="h-4 w-4" />
                  <span>Stale Lock Detected</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  The previous invocation appears to have stalled or crashed. 
                  You can force a takeover to resume processing.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onForceTakeover}
                  className="gap-1"
                >
                  <RotateCcw className="h-3 w-3" />
                  Force Takeover
                </Button>
              </div>
            )}

            {/* Copy button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyDiagnostics}
                className="gap-1 text-xs h-7"
              >
                {copied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied!" : "Copy diagnostics JSON"}
              </Button>
            </div>

            {/* CR184: Lock State */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Concurrency Lock
              </div>
              <div className="text-xs font-mono bg-muted p-2 rounded space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={lockStatus.variant} className="text-[10px] gap-1">
                    {lockStatus.icon}
                    {lockStatus.label}
                  </Badge>
                </div>
                {lockOwner && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Owner:</span>
                    <span className="font-medium">{lockOwner.slice(0, 8)}</span>
                  </div>
                )}
                {previousOwner && lockOutcome === "ACQUIRED_STALE_TAKEOVER" && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Prev owner:</span>
                    <span className="text-destructive">{previousOwner.slice(0, 8)} (stale {previousHeartbeatAge}s)</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">TTL:</span>
                  <span>{lockTtl}s</span>
                </div>
              </div>
            </div>

            {/* Phase & Heartbeat */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Execution State
              </div>
              <div className="text-xs font-mono bg-muted p-2 rounded space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Phase:</span>
                  <span className={stepStatus === "RUNNING" && isHeartbeatStale ? "text-destructive font-bold" : ""}>
                    {phase}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Heartbeat:</span>
                  <span className={isHeartbeatStale ? "text-destructive font-bold" : ""}>
                    {heartbeatAge !== null ? `${heartbeatAge}s ago` : "—"}
                    {isHeartbeatStale && ` (STALE! TTL=${lockTtl}s)`}
                  </span>
                </div>
                {invocationId && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Invocation:</span>
                    <span className="text-muted-foreground/70">{invocationId.slice(0, 8)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Rule Pack (Normalize) */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Normalize Rule Pack (Policy Rules)
              </div>
              <div className="text-xs font-mono bg-muted p-2 rounded space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={resolverStatus.variant} className="text-[10px] gap-1">
                    {resolverStatus.icon}
                    {resolverStatus.label}
                  </Badge>
                  {resolverMs !== undefined && (
                    <span className="text-muted-foreground">({resolverMs}ms)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">ID:</span>
                  <span>{rulePackIdUsed || "(none / embedded)"}</span>
                </div>
                {rulePackName && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Name:</span>
                    <span>{rulePackName}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Source:</span>
                  <span>{rulePackSource || "pending"}</span>
                </div>
              </div>
            </div>

            {/* Extraction Prompt Packs (from staging) */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Extraction Prompt Packs (AI Extraction)
              </div>
              <div className="text-xs font-mono bg-muted p-2 rounded">
                {samplePromptPacks.length > 0 ? (
                  <div className="space-y-1">
                    {samplePromptPacks.map((sample, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-[11px]">
                        <span className="text-muted-foreground">{sample.source_type}:</span>
                        <span className="font-medium">
                          {sample.prompt_pack_id 
                            ? `${sample.prompt_pack_id.slice(0, 8)}...${sample.prompt_pack_version ? ` v${sample.prompt_pack_version}` : ""}`
                            : "(none)"}
                        </span>
                        <span className="text-muted-foreground/70">({sample.merchant_key})</span>
                      </div>
                    ))}
                    <div className="text-muted-foreground/60 italic mt-1">
                      Sampled from {samplePromptPacks.length} staging items
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground italic">No samples available yet</span>
                )}
              </div>
            </div>

            {/* Timers */}
            {Object.keys(timersMs).length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Phase Timings (ms)
                </div>
                <div className="text-xs font-mono bg-muted p-2 rounded">
                  {Object.entries(timersMs).map(([phase, ms]) => (
                    <div key={phase} className="flex items-center gap-2">
                      <span className="text-muted-foreground">{phase}:</span>
                      <span>{ms}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
